import {
  useCallback, useMemo,
} from 'react';
import {
  isGroupingColumnNumeric, isGroupingColumnText,
} from '~/_shared/types/grouping/grouping';
import {
  getNumberColumnBucketId, getTextColumnGroupIndex,
} from '~/_shared/utils/grouping/grouping.helpers';
import { notNullsy } from '~/_shared/utils/typeGuards';
import {
  useFilteredLatLngSpreadsheetData, useLatLngSpreadsheetData,
} from '~/map/map/useSpreadsheetData.hook';
import { useMapComponentLastBoundsSelector } from '~/store/frontendState/mapComponent/mapComponent.selectors';
import { useMapSettingsGroupingActiveGroupColumnsSelector } from '~/store/mapSettings/grouping/mapSettingsGrouping.selectors';
import { useSpreadsheetDataDataSelector } from '~/store/spreadsheetData/spreadsheetData.selectors';
import {
  DataType, type GroupId as TextGroupId, Unfiltered,
} from '~/store/spreadsheetData/spreadsheetData.state';

type GroupId = TextGroupId | number;

export const useVisibleGroupIdsAtHierarchy = (hierarchyIndicator = 0): ReadonlySet<GroupId> => {
  const spreadsheetData = useSpreadsheetDataDataSelector();
  const activeGroupColumns = useMapSettingsGroupingActiveGroupColumnsSelector();
  const locationData = useLatLngSpreadsheetData();
  const lastBounds = useMapComponentLastBoundsSelector()?.bounds;
  const visibleMarkers = useFilteredLatLngSpreadsheetData().visible;

  const filteredLocationData = useMemo(() =>
    locationData.data
      .filter(row => {
        const checkBounds = lastBounds?.contains(row);
        const checkVisibility = !!visibleMarkers.getRow(row);

        return checkBounds && checkVisibility;
      }),
  [locationData.data, lastBounds, visibleMarkers]);

  const groupingColumn = activeGroupColumns?.[hierarchyIndicator];

  const getGroup = useCallback((rowId: string): GroupId | null => {
    if (!spreadsheetData || !groupingColumn || !filteredLocationData.length) {
      return null;
    }

    const { spreadsheetId, columnId } = groupingColumn;

    if (isGroupingColumnNumeric(groupingColumn)) {
      const bucket = getNumberColumnBucketId(spreadsheetData, spreadsheetId, columnId, rowId, groupingColumn);

      return bucket.id;
    }
    else if (isGroupingColumnText(groupingColumn)) {
      const uniqueGroupIndex = getTextColumnGroupIndex(spreadsheetData, spreadsheetId, columnId, rowId);

      const groupData =
        spreadsheetData.values[spreadsheetId]?.[Unfiltered]?.[columnId]?.[DataType.GROUP]?.extra.uniqueGroups[uniqueGroupIndex] ?? null;

      if (groupData) {
        return groupData.id;
      }
    }

    return null;
  }, [groupingColumn, spreadsheetData, filteredLocationData.length]);

  return useMemo<ReadonlySet<GroupId>>(() => {
    const result = new Set<GroupId>();

    filteredLocationData.forEach(({ spreadsheetId: rowSpreadsheetId, rowId }) => {
      if (rowSpreadsheetId === groupingColumn?.spreadsheetId) {
        const groupId = getGroup(rowId);
        if (notNullsy(groupId)) {
          result.add(groupId);
        }
      }
    });

    return result;
  }, [filteredLocationData, groupingColumn?.spreadsheetId, getGroup]);

};
